import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { triggerERPExecution, getAllEmergencyResponsePlans, getAllBCPPlans } from '../../../service/api';

const TriggerERP = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        planType: 'ERP',
        erpId: '',
        bcpId: '',
        incidentId: '',
        reason: ''
    });
    const [plans, setPlans] = useState<{
        erpPlans: any[];
        bcpPlans: any[];
    }>({
        erpPlans: [],
        bcpPlans: []
    });

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            const [erpRes, bcpRes] = await Promise.all([
                getAllEmergencyResponsePlans(),
                getAllBCPPlans()
            ]);

            // Extract ERP plans with proper validation
            const erpPlans = (erpRes.data?.resCode === 200 && erpRes.data?.data)
                ? (Array.isArray(erpRes.data.data) ? erpRes.data.data : [])
                : [];

            // Extract BCP plans with proper validation
            const bcpPlans = (bcpRes.data?.resCode === 200 && bcpRes.data?.data)
                ? (Array.isArray(bcpRes.data.data) ? bcpRes.data.data : [])
                : [];
            setPlans({
                erpPlans,
                bcpPlans
            });
        } catch (error) {
            console.error('Error fetching plans:', error);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (formData.planType === 'ERP' && !formData.erpId) {
            alert('Please select an Emergency Response Plan');
            return;
        }

        if (formData.planType === 'BCP' && !formData.bcpId) {
            alert('Please select a Business Continuity Plan');
            return;
        }

        if (!window.confirm('Are you sure you want to trigger this plan? This will notify all assigned team members.')) {
            return;
        }

        try {
            setLoading(true);

            const payload = {
                erpId: formData.planType === 'ERP' ? formData.erpId : undefined,
                bcpId: formData.planType === 'BCP' ? formData.bcpId : undefined,
                incidentId: formData.incidentId || undefined,
                reason: formData.reason
            };

            const response = await triggerERPExecution(payload);

            if (response.data.success) {
                alert('ERP execution triggered successfully!');
                navigate(`/external-emergency-preparedness/erp-executions/${response.data.data.executionId}`);
            }
        } catch (error) {
            console.error('Error triggering execution:', error);
            alert('Failed to trigger execution. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = {
        width: '100%',
        padding: '10px',
        borderRadius: '6px',
        border: '1px solid #d1d5db',
        fontSize: '14px'
    };

    const labelStyle = {
        display: 'block',
        marginBottom: '6px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#374151'
    };

    return (
        <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ marginBottom: '24px' }}>
                <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
                    Trigger Emergency Response Plan
                </h1>
                <p style={{ color: '#6b7280', fontSize: '14px' }}>
                    Activate an emergency response or business continuity plan
                </p>
            </div>

            <form onSubmit={handleSubmit} style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                {/* Plan Type */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={labelStyle}>Plan Type</label>
                    <div style={{ display: 'flex', gap: '16px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                                type="radio"
                                name="planType"
                                value="ERP"
                                checked={formData.planType === 'ERP'}
                                onChange={(e) => setFormData({ ...formData, planType: e.target.value })}
                                style={{ marginRight: '6px' }}
                            />
                            <span style={{ fontSize: '14px' }}>Emergency Response Plan</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                                type="radio"
                                name="planType"
                                value="BCP"
                                checked={formData.planType === 'BCP'}
                                onChange={(e) => setFormData({ ...formData, planType: e.target.value })}
                                style={{ marginRight: '6px' }}
                            />
                            <span style={{ fontSize: '14px' }}>Business Continuity Plan</span>
                        </label>
                    </div>
                </div>

                {/* Plan Selection */}
                {formData.planType === 'ERP' && (
                    <div style={{ marginBottom: '20px' }}>
                        <label style={labelStyle}>
                            Select Emergency Response Plan <span style={{ color: '#dc2626' }}>*</span>
                        </label>
                        <select
                            value={formData.erpId}
                            onChange={(e) => setFormData({ ...formData, erpId: e.target.value })}
                            style={inputStyle}
                            required
                        >
                            <option value="">-- Select Plan --</option>
                            {plans.erpPlans.map(plan => (
                                <option key={plan._id} value={plan._id}>
                                    {plan.erpName || plan.planName} - {plan.scenarioType}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {formData.planType === 'BCP' && (
                    <div style={{ marginBottom: '20px' }}>
                        <label style={labelStyle}>
                            Select Business Continuity Plan <span style={{ color: '#dc2626' }}>*</span>
                        </label>
                        <select
                            value={formData.bcpId}
                            onChange={(e) => setFormData({ ...formData, bcpId: e.target.value })}
                            style={inputStyle}
                            required
                        >
                            <option value="">-- Select Plan --</option>
                            {plans.bcpPlans.map(plan => (
                                <option key={plan._id} value={plan._id}>
                                    {plan.planName}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Incident ID (Optional) */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={labelStyle}>Incident ID (Optional)</label>
                    <input
                        type="text"
                        value={formData.incidentId}
                        onChange={(e) => setFormData({ ...formData, incidentId: e.target.value })}
                        placeholder="Link to existing incident"
                        style={inputStyle}
                    />
                </div>

                {/* Reason */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={labelStyle}>Reason for Activation</label>
                    <textarea
                        value={formData.reason}
                        onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                        placeholder="Describe why this plan is being activated..."
                        rows={4}
                        style={{ ...inputStyle, resize: 'vertical' }}
                    />
                </div>

                {/* Warning */}
                <div style={{
                    padding: '16px',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    marginBottom: '20px'
                }}>
                    <p style={{ margin: 0, fontSize: '14px', color: '#991b1b' }}>
                        <strong>⚠️ Warning:</strong> Triggering this plan will immediately notify all assigned team members and begin execution tracking. Ensure this action is necessary.
                    </p>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button
                        type="button"
                        onClick={() => navigate('/external-emergency-preparedness/erp-executions')}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#e5e7eb',
                            color: '#374151',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '500'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: loading ? '#9ca3af' : '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontWeight: '500'
                        }}
                    >
                        {loading ? 'Triggering...' : 'Trigger Plan'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default TriggerERP;
